/**
 * Helpers for the infinite "mails list" cache. All pure, except `nextFetchSeq`
 * which owns the module-level page counter.
 */
import Email from "@/domain/entities/emails/Email";
import EmailAttachment from "@/domain/entities/emails/EmailAttachement";
import LinkObject from "@/domain/entities/emails/LinkObject";
import { isSameCopy } from "@/lib/mailIdentity";
import type { InfiniteData } from "@tanstack/react-query";

export type MailsPage = {
    data: Email[];
    currentPage: number;
    nextPage: number | null;
    /** Distingue une page fraîche d'une page rejouée. Voir `rebaseStalePages`. */
    fetchSeq?: number;
};

let lastFetchSeq = 0;

/** Numéro d'ordre du prochain fetch de page. */
export const nextFetchSeq = (): number => {
    lastFetchSeq += 1;

    return lastFetchSeq;
};

/**
 * Une page périmée ne doit jamais écraser le cache courant : `useInfiniteQuery`
 * reconstruit `data.pages` depuis l'instantané pris au départ du fetch, donc toute
 * écriture faite pendant le vol serait perdue.
 *
 * Une écriture issue d'un fetch apporte au moins une page de `fetchSeq` inconnu ;
 * une écriture issue d'un `setQueryData` recopie les seq existants et ne doit pas
 * être rebasée, sous peine d'annuler son propre patch.
 */
export const rebaseStalePages = (
    previous: MailsListCache | undefined,
    fresh: MailsListCache,
): MailsListCache => {
    const previousPages = previous?.pages;
    if (!previousPages?.length || !fresh?.pages?.length) return fresh;

    const known = new Map<number, MailsPage>();
    previousPages.forEach((page) => {
        if (typeof page?.fetchSeq === "number") {
            known.set(page.fetchSeq, page);
        }
    });
    if (known.size === 0) return fresh;

    const comesFromAFetch = fresh.pages.some(
        (page) =>
            typeof page?.fetchSeq === "number" && !known.has(page.fetchSeq),
    );
    if (!comesFromAFetch) return fresh;

    let rebased = false;
    const pages = fresh.pages.map((page) => {
        if (typeof page?.fetchSeq !== "number") return page;

        const current = known.get(page.fetchSeq);
        if (!current || current === page) return page;

        rebased = true;

        return current;
    });

    return rebased ? { ...fresh, pages } : fresh;
};

export type MailsListCache = InfiniteData<MailsPage>;

/** Fields a mail action can optimistically patch on the cached copies. */
export type MailStatusPatch = Partial<
    Pick<Email, "isRead" | "isUserProcessed" | "isFocus">
>;

/** Patch every cached copy matching one of `mails` (by physical copy identity). */
export const updateMailsInList = (
    list: MailsListCache,
    mails: Email[],
    patch: MailStatusPatch,
): MailsListCache => ({
    ...list,
    pages: list.pages.map((page) => ({
        ...page,
        data: page.data?.map((mailFromPage) =>
            mails.some((mail) => isSameCopy(mail, mailFromPage))
                ? new Email({ ...mailFromPage, ...patch })
                : mailFromPage,
        ),
    })),
});

// Removal matches by SDK `id` (not `uniqueMailvistaId`) to preserve the
// historical behaviour of the delete / leave-list actions.
export const removeMailsFromList = (
    list: MailsListCache,
    mails: Email[],
): MailsListCache => ({
    ...list,
    pages: list.pages.map((page) => ({
        ...page,
        data: page.data.filter(
            (mail) => !mails.some((toRemove) => toRemove.id === mail.id),
        ),
    })),
});

// Undo a failed bulk action on the live cache (concurrent-safe): replace matching copies, re-insert removed ones.
export const restoreMailsInList = (
    list: MailsListCache,
    mails: Email[],
): MailsListCache => {
    if (mails.length === 0) return list;

    const originals = new Map(
        mails.map((mail) => [mail.uniqueMailvistaId, mail]),
    );
    const restored = new Set<string>();
    const pages = list.pages.map((page) => ({
        ...page,
        data: page.data.map((mail) => {
            const original = originals.get(mail.uniqueMailvistaId);
            if (!original) return mail;
            restored.add(mail.uniqueMailvistaId);

            return original;
        }),
    }));

    const removed = mails.filter(
        (mail) => !restored.has(mail.uniqueMailvistaId),
    );
    if (removed.length === 0 || pages.length === 0) {
        return { ...list, pages };
    }

    return {
        ...list,
        pages: pages.map((page, index) =>
            index === 0 ? { ...page, data: [...removed, ...page.data] } : page,
        ),
    };
};

/**
 * Champs peuplés par l'enrichissement asynchrone, après le listing : un snapshot
 * encore vide écraserait une copie déjà enrichie et ferait chuter les compteurs de
 * l'encart « liens et documents ».
 */
type EnrichedFields = {
    attachments?: EmailAttachment[];
    links?: LinkObject[];
};

/** `true` si la valeur enrichie du cache doit être réinjectée dans le snapshot frais. */
const shouldPreserveAttachments = (fresh: Email): boolean =>
    // Enrichissement PJ en attente : le listing porte hasAttachment=true + attachments=[].
    // Un mail dont les PJ ont réellement disparu revient hasAttachment=false (le SDK corrige
    // le flag) → on laisse passer le snapshot vide et le compteur redescend.
    fresh.hasAttachment === true && !fresh.attachments?.length;

const shouldPreserveLinks = (fresh: Email): boolean =>
    // Les liens sont dérivés du corps (immuable pour un mail reçu) : ils ne passent jamais
    // légitimement de peuplé à vide dans une session, donc préserver une valeur non vide du
    // cache face à un snapshot vide est sûr (pas de flag « hasLinks » côté SDK).
    !fresh.links?.length;

/**
 * Réinjecte dans une page fraîchement fetchée les champs d'enrichissement (PJ, liens)
 * déjà connus du cache, pour qu'un snapshot pré-enrichissement ne les écrase pas.
 * Le parcours du cache reste O(taille de la liste chargée) par fetch ; on ne mémorise
 * dans `known` que les copies dont un champ est réellement en attente dans la page
 * fraîche, ce qui borne la mémoire, pas le nombre de mails visités. Impact négligeable
 * sur un chemin réseau-bound.
 */
export const preserveEnrichedFields = (
    freshMails: Email[],
    previousList: MailsListCache | undefined,
): Email[] => {
    if (!previousList?.pages) return freshMails;

    const pendingIds = new Set<string>();
    freshMails.forEach((mail) => {
        if (shouldPreserveAttachments(mail) || shouldPreserveLinks(mail)) {
            pendingIds.add(mail.uniqueMailvistaId);
        }
    });
    if (pendingIds.size === 0) return freshMails;

    const known = new Map<string, EnrichedFields>();
    previousList.pages.forEach((page) =>
        page.data?.forEach((mail) => {
            if (!pendingIds.has(mail.uniqueMailvistaId)) return;

            const entry = known.get(mail.uniqueMailvistaId) ?? {};
            if (mail.attachments?.length && !entry.attachments) {
                entry.attachments = mail.attachments;
            }
            if (mail.links?.length && !entry.links) {
                entry.links = mail.links;
            }
            known.set(mail.uniqueMailvistaId, entry);
        }),
    );
    if (known.size === 0) return freshMails;

    return freshMails.map((mail) => {
        const cached = known.get(mail.uniqueMailvistaId);
        if (!cached) return mail;

        const patch: EnrichedFields = {};
        if (cached.attachments && shouldPreserveAttachments(mail)) {
            patch.attachments = cached.attachments;
        }
        if (cached.links && shouldPreserveLinks(mail)) {
            patch.links = cached.links;
        }
        if (!patch.attachments && !patch.links) return mail;

        return new Email({ ...mail, ...patch });
    });
};

/** Loaded pages are all empty but the server still has more to fetch. */
export const listIsEmptyWithMore = (list: MailsListCache): boolean => {
    const hasVisibleMails = list.pages.some((page) => page.data.length > 0);
    const lastPage = list.pages[list.pages.length - 1];
    const hasMoreToFetch = lastPage?.nextPage != null;

    return !hasVisibleMails && hasMoreToFetch;
};
